import { makeStyles } from 'tss-react/mui';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import { momentParse } from '../../utils/time';
import MaterialLink from '../material-link';
import { useAppContext } from '../../contexts/app';
import DeleteCommentLink from './delete-comment-link';
import Markdown from '../markdown';
import { eventStatusName } from '../../utils/event-status';
import type { EventComment as EventCommentType } from '../../types/EventComment';
import type { Event } from '../../types/Event';

const useStyles = makeStyles()((theme) => ({
  meta: {
    display: 'flex'
  },
  avatarLink: {
    marginRight: theme.spacing(1)
  },
  displayName: {
    display: 'flex',
    alignItems: 'center'
  },
  chip: {
    marginLeft: theme.spacing(1)
  },
  statusChip: {
    borderColor: `rgba(0, 0, 0, 0.2)`,
    color: theme.palette.text.primary
  },
  subName: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    color: theme.palette.text.primary
  },
  createdAt: {
    paddingTop: 1
  },
  deleteLink: {
    marginLeft: theme.spacing(1),
    '& > button': {
      paddingBottom: 2
    }
  },
  body: {
    margin: theme.spacing(2, 0, 2, 0),
  },
}));

type Props = {
  comment?: EventCommentType;
  event?: Event;
};

const EventComment = ({ comment, event }: Props) => {
  const { isLoggedIn } = useAppContext();
  const { classes, cx } = useStyles();
  const { membership } = comment;
  const createdAt = momentParse(comment.createdAt);

  return (
    <div>
      <div className={classes.meta}>
        <MaterialLink
          href={`/people/${comment.user.username}`}
          className={classes.avatarLink}
        >
          <Avatar
            alt={comment.user.displayName}
            src={comment.user.avatar.small.url}
          />
        </MaterialLink>
        <div>
          <div className={classes.displayName}>
            <MaterialLink
              href={`/people/${comment.user.username}`}
            >
              {comment.user.displayName}
            </MaterialLink>

            {membership.isHost && (
              <Chip
                size="small"
                label="Host"
                className={classes.chip}
                color="primary"
              />
            )}
            {membership.status && (
              <Chip
                size="small"
                variant="outlined"
                label={eventStatusName(membership.status)}
                className={cx(classes.chip, classes.statusChip)}
              />
            )}
          </div>
          <div className={classes.subName}>
            <time
              className={classes.createdAt}
              dateTime={createdAt.toString()}
              title={createdAt.format('dddd, MMMM Do, YYYY h:mm a')}
            >
              {createdAt.fromNow()}
            </time>
            {isLoggedIn && (
              <DeleteCommentLink comment={comment} event={event} />
            )}
          </div>
        </div>
      </div>
      <div className={classes.body}>
        <Markdown>{comment.body}</Markdown>
      </div>
    </div>
  );
};

export default EventComment;
